import React from 'react';
import { Bar } from '@nivo/bar';
import { useTextMeasurer } from 'helpers/dom';
import { Loading, SystemText } from 'e4shared';

export const ParticipantCount = ({ data, consts, divHeight }) => {
    const [mappedData, setMappedData] = React.useState([]);
    const [width, setWidth] = React.useState(0);
    const [maxValue, setMaxValue] = React.useState(100);
    const truncate = useTextMeasurer('12px sans-serif');

    const seller = SystemText('Dashboard/Widgets/ActionPlanner/Charts/Participant/Seller');
    const manager = SystemText('Dashboard/Widgets/ActionPlanner/Charts/Participant/Manager');

    const roundUpRelevant = num => {
        if (num < 100) {
            // round up to next 10
            return Math.ceil(num / 10) * 10;
        } else if (num < 1000) {
            // round up to next 100
            return Math.ceil(num / 100) * 100;
        } else if (num < 10000) {
            // round up to next 1000
            return Math.ceil(num / 1000) * 1000;
        } else {
            // round up to next 10000
            return Math.ceil(num / 10000) * 10000;
        }
    };

    React.useEffect(() => {
        let max = 0;
        const temp = data.map(d => {
            const item = { location: d.x };
            item[seller] = d.y;
            item[manager] = d.z;
            max = d.y + d.z > max ? d.y + d.z : max;
            return item;
        });

        setMaxValue(roundUpRelevant(max));
        setMappedData(temp.sort((a, b) => (a.location > b.location ? -1 : 1)));
    }, [data]);

    React.useEffect(() => {
        if (data?.length) {
            const parent = document.getElementById(consts.parent);
            setWidth(parent.offsetWidth);
        }
    }, [data]);

    if (!width) {
        return <Loading />;
    }

    return (
        <div style={data.length > 10 ? { height: `${divHeight(mappedData)}px`, overflowY: 'auto' } : null}>
            <Bar
                data={mappedData}
                indexBy="location"
                keys={[seller, manager]}
                colors={['#21154A', '#8C81A1']}
                borderRadius={4}
                layout="horizontal"
                groupMode="stacked"
                enableGridX={true}
                enableGridY={false}
                height={divHeight(mappedData, true)}
                width={width - consts.leftMargin}
                padding={consts.padding}
                margin={{ top: 0, right: 100, bottom: consts.axisHeight, left: consts.leftMargin }}
                valueScale={{
                    type: 'linear',
                    min: 0,
                    max: maxValue,
                    clamp: true, // Prevents bars from overflowing
                }}
                axisBottom={{
                    tickSize: 0,
                    tickPadding: 5,
                    tickRotation: 0,
                    tickValues: 6,
                }}
                axisLeft={{
                    renderTick: tick => (
                        <g dominantBaseline="central" transform={`translate(${tick.x},${tick.y})`}>
                            <text
                                transform={`translate(-${consts.leftMargin}, 0)`}
                                textAnchor="left" // Ensures left-aligned text
                                style={{
                                    color: '#333',
                                    fontSize: 11,
                                }}
                            >
                                {truncate(tick.value, consts.leftMargin - 5)}
                            </text>
                        </g>
                    ),
                }}
                enableLabel={false}
                layers={[
                    'grid',
                    'axes',
                    'bars',
                    'markers',
                    'legends',
                    'annotations',
                    props => {
                        // custom label layer
                        return props.bars
                            .filter(bar => bar.data.indexValue)
                            .map((barGroup, i) => {
                                // group bars by indexValue
                                const stackBars = props.bars.filter(b => b.data.indexValue === barGroup.data.indexValue);
                                const total = stackBars.reduce((sum, b) => sum + b.data.value, 0);
                                // position at the end of the stack
                                const validBars = stackBars.filter(b => b.data.value);
                                const lastBar = validBars?.length ? validBars[validBars.length - 1] : null;
                                if (!total || !lastBar) return null;
                                return (
                                    <text
                                        key={i}
                                        x={lastBar.x + lastBar.width + 5} // a bit after bar end
                                        y={lastBar.y + lastBar.height / 2}
                                        textAnchor="start"
                                        dominantBaseline="middle"
                                        style={{ fontSize: 12, fill: '#333' }}
                                    >
                                        {total}
                                    </text>
                                );
                            });
                    },
                ]}
                legends={[
                    {
                        dataFrom: 'keys', // use the bar keys
                        anchor: 'right', // place it on the right side
                        direction: 'column', // vertical list
                        justify: false,
                        translateX: 120, // move it right
                        translateY: 30, // adjust vertical position
                        itemsSpacing: 8,
                        itemWidth: 100, // width of each legend item
                        itemHeight: 20,
                        itemDirection: 'left-to-right',
                        symbolSize: 20, // size of color square
                        symbolShape: 'square',
                    },
                ]}
            />
        </div>
    );
};

export default ParticipantCount;
